import "@/App.css";
import { useEffect } from "react";
import { api } from "./lib/api";
import { BrowserRouter, Routes, Route, Navigate, useLocation } from "react-router-dom";
import { Toaster } from "@/components/ui/sonner";
import { AuthProvider, useAuth } from "./context/AuthContext";
import { CartProvider } from "./context/CartContext";
import { QuoteProvider } from "./context/QuoteContext";
import { CurrencyProvider } from "./context/CurrencyContext";
import { Navbar } from "./components/Navbar";
import { Footer } from "./components/Footer";
import Landing from "./pages/Landing";
import Offres from "./pages/Offres";
import Catalogue from "./pages/Catalogue";
import ProductDetail from "./pages/ProductDetail";
import Cart from "./pages/Cart";
import Checkout from "./pages/Checkout";
import PaymentResult from "./pages/PaymentResult";
import AuthPage from "./pages/AuthPage";
import AuthCallback from "./pages/AuthCallback";
import ClientSpace from "./pages/ClientSpace";
import AdminDashboard from "./pages/AdminDashboard";
import TrackOrder from "./pages/TrackOrder";
import Devis from "./pages/Devis";
import DevisSign from "./pages/DevisSign";
import Expertises from "./pages/Expertises";
import DevInformatique from "./pages/DevInformatique";
import SatisfactionSurvey from "./pages/SatisfactionSurvey";
import AdminPreview from "./pages/AdminPreview";
import PrendreRDV from "./pages/PrendreRDV";
import DemoRequest from "./pages/DemoRequest";
import Offre from "./pages/Offre";
import HubOffres from "./pages/HubOffres";
import VideosWhatsApp from "./pages/VideosWhatsApp";
import Plaquette from "./pages/Plaquette";
import ComparatifSolutions from "./pages/ComparatifSolutions";
import VisioSession from "./pages/VisioSession";
import Teleconsultation from "./pages/Teleconsultation";
import IntranetSpace from "./pages/IntranetSpace";
import Webinaires from "./pages/Webinaires";
import WebinaireEvent from "./pages/WebinaireEvent";
import WebinaireAcces from "./pages/WebinaireAcces";
import WebinaireAnimer from "./pages/WebinaireAnimer";
import Lms from "./pages/Lms";
import LmsCourse from "./pages/LmsCourse";
import VerifyCertificate from "./pages/VerifyCertificate";
import VerifyPlagiat from "./pages/VerifyPlagiat";
import VerifyArticle from "./pages/VerifyArticle";
import VerifyCourrier from "./pages/VerifyCourrier";
import SatisfactionPublic from "./pages/SatisfactionPublic";
import QualitePatient, { QUALITE_ROLES } from "./pages/QualitePatient";
import PilotageSih, { SIH_ROLES } from "./pages/PilotageSih";
import SignDevis from "./pages/SignDevis";
import RechercheScientifique from "./pages/RechercheScientifique";
import AtelierEcriture from "./pages/AtelierEcriture";
import TableauDeBord from "./pages/TableauDeBord";
import PmsiCouverture, { PMSI_ROLES } from "./pages/PmsiCouverture";
import DirectionGenerale, { DIRECTION_ROLES } from "./pages/DirectionGenerale";
import Courriers from "./pages/Courriers";
import SharedCvReview from "./pages/SharedCvReview";
import LmsPaiement from "./pages/LmsPaiement";
import WebinairePaiement from "./pages/WebinairePaiement";
import { FloatingWhatsApp } from "./components/WhatsApp";
import { EmmyWidget } from "./components/emmy/EmmyWidget";
import { applyTheme } from "./components/admin/ThemeSelector";
import { BackBar } from "./components/BackButton";

function RequireAuth({ roles, children }) {
  const { user } = useAuth();
  const location = useLocation();
  if (user === null) {
    return (
      <div className="flex min-h-screen items-center justify-center" data-testid="auth-loading">
        <div className="h-8 w-8 animate-spin rounded-full border-2 border-slate-300 border-t-ciel" />
      </div>
    );
  }
  if (user === false) return <Navigate to="/auth" state={{ from: location.pathname }} replace />;
  if (roles && !roles.includes(user.role)) return <Navigate to="/" replace />;
  return children;
}

function AppRouter() {
  const location = useLocation();
  // Détection synchrone du fragment OAuth avant tout rendu de route
  if (location.hash?.includes("session_id=")) {
    return <AuthCallback />;
  }
  // Routes visio en plein écran (fenêtre de présentation dédiée, sans navbar ni footer)
  if (location.pathname.startsWith("/visio/") || location.pathname.startsWith("/admin/visio/consultation/")) {
    return (
      <Routes>
        <Route path="/visio/:token" element={<VisioSession />} />
        <Route path="/admin/visio/consultation/:id" element={<Teleconsultation />} />
      </Routes>
    );
  }
  // Espace intranet professionnels (plein écran, sans navbar ni footer)
  if (location.pathname.startsWith("/intranet")) {
    return (
      <Routes>
        <Route path="/intranet" element={<RequireAuth roles={["professionnel"]}><IntranetSpace /></RequireAuth>} />
      </Routes>
    );
  }
  return (
    <>
      <Navbar />
      <main className="min-h-screen pt-16">
        {location.pathname !== "/" && <BackBar />}
        <Routes>
          <Route path="/" element={<Landing />} />
          <Route path="/offres" element={<Offres />} />
          <Route path="/catalogue" element={<Catalogue />} />
          <Route path="/produit/:id" element={<ProductDetail />} />
          <Route path="/panier" element={<Cart />} />
          <Route path="/devis" element={<Devis />} />
          <Route path="/devis/signature/:token" element={<DevisSign />} />
          <Route path="/suivi" element={<TrackOrder />} />
          <Route path="/expertises" element={<Expertises />} />
          <Route path="/developpement-informatique" element={<DevInformatique />} />
          <Route path="/enquete" element={<SatisfactionSurvey />} />          <Route path="/prendre-rendez-vous" element={<PrendreRDV />} />
          <Route path="/webinaires" element={<Webinaires />} />
          <Route path="/webinaire/paiement" element={<WebinairePaiement />} />
          <Route path="/webinaire/acces/:token" element={<WebinaireAcces />} />
          <Route path="/webinaire/animer/:hostToken" element={<WebinaireAnimer />} />
          <Route path="/webinaire/:id" element={<WebinaireEvent />} />
          <Route path="/cours" element={<Lms />} />
          <Route path="/cours/paiement" element={<LmsPaiement />} />
          <Route path="/cours/:id" element={<LmsCourse />} />
          <Route path="/verifier-certificat/:code" element={<VerifyCertificate />} />
          <Route path="/verifier-plagiat/:code" element={<VerifyPlagiat />} />
          <Route path="/verifier-article/:code" element={<VerifyArticle />} />
          <Route path="/verifier-courrier/:token" element={<VerifyCourrier />} />
          <Route path="/satisfaction/:etabId" element={<SatisfactionPublic />} />
          <Route path="/qualite" element={<RequireAuth roles={QUALITE_ROLES}><QualitePatient /></RequireAuth>} />
          <Route path="/signer-devis/:token" element={<SignDevis />} />
          <Route path="/cv/partage/:token" element={<SharedCvReview />} />
          <Route path="/demander-demo" element={<DemoRequest />} />
          <Route path="/nos-offres" element={<HubOffres />} />
          <Route path="/videos-whatsapp" element={<VideosWhatsApp />} />
          <Route path="/plaquette" element={<Plaquette />} />
          <Route path="/offres/:slug" element={<Offre />} />
          <Route path="/solutions/comparatif" element={<ComparatifSolutions />} />
          <Route path="/visio/:token" element={<VisioSession />} />
          <Route path="/admin/visio/consultation/:id" element={<Teleconsultation />} />
          <Route path="/checkout" element={<RequireAuth><Checkout /></RequireAuth>} />
          <Route path="/paiement/succes" element={<PaymentResult success />} />
          <Route path="/paiement/annule" element={<PaymentResult />} />
          <Route path="/auth" element={<AuthPage />} />
          <Route path="/compte" element={<RequireAuth roles={["client", "admin"]}><ClientSpace /></RequireAuth>} />
          <Route path="/recherche-scientifique" element={<RequireAuth roles={["client", "professionnel", "admin", "medecin_dim", "tim", "gestionnaire_donnees"]}><RechercheScientifique /></RequireAuth>} />
          <Route path="/atelier-ecriture" element={<RequireAuth roles={["client", "professionnel", "admin", "medecin_dim", "tim", "gestionnaire_donnees"]}><AtelierEcriture /></RequireAuth>} />
          <Route path="/pilotage" element={<RequireAuth roles={["gestionnaire", "admin"]}><TableauDeBord /></RequireAuth>} />
          <Route path="/pilotage-sih" element={<RequireAuth roles={SIH_ROLES}><PilotageSih /></RequireAuth>} />
          <Route path="/pmsi" element={<RequireAuth roles={PMSI_ROLES}><PmsiCouverture /></RequireAuth>} />
          <Route path="/direction" element={<RequireAuth roles={DIRECTION_ROLES}><DirectionGenerale /></RequireAuth>} />
          <Route path="/courriers" element={<RequireAuth><Courriers /></RequireAuth>} />
          <Route path="/admin" element={<RequireAuth roles={["admin"]}><AdminDashboard /></RequireAuth>} />
          <Route path="/admin/apercu/:id" element={<RequireAuth roles={["admin"]}><AdminPreview /></RequireAuth>} />
          <Route path="*" element={<Navigate to="/" replace />} />
        </Routes>
      </main>
      <Footer />
    </>
  );
}

function App() {
  useEffect(() => {
    applyTheme(localStorage.getItem("vitalis_theme") || "bleu");
    api.get("/theme").then(({ data }) => applyTheme(data.theme)).catch(() => {});
  }, []);
  return (
    <div className="App">
      <BrowserRouter>
        <AuthProvider>
          <CurrencyProvider>
            <CartProvider>
              <QuoteProvider>
                <AppRouter />
                <Toaster position="top-right" richColors closeButton />
                <FloatingWhatsApp />
                <EmmyWidget />
              </QuoteProvider>
            </CartProvider>
          </CurrencyProvider>
        </AuthProvider>
      </BrowserRouter>
    </div>
  );
}

export default App;
